import { Button, Card, Center, Stack, Text, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LOGIN_METHOD, type LoginChallengeResult, type LoginChallengeUserResponse } from '@/api';
import { DocumentTitle } from '@/components/Ui/DocumentTitle';
import { useLanguageAwareRouting } from '../hooks/useLanguageAwareRouting';

export interface MagicLinkLoginPageProps {
  loginChallenge: (props: LoginChallengeUserResponse) => Promise<LoginChallengeResult>;
  onLoginSuccess?: () => void; // If not provided, will navigate to '/account'
}

export function MagicLinkLoginPage(props: MagicLinkLoginPageProps) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<string[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();

  const { loginChallenge, onLoginSuccess } = props;

  const submitChallenge = async (id: string, challenge: string) => {
    setIsBusy(true);
    try {
      const result = await loginChallenge({
        type: LOGIN_METHOD.EMAIL_MAGIC_LINK,
        content: {
          id,
          challenge,
        },
      });
      setIsBusy(false);
      // The stored post-login path stays put; the code step's success consumes it.
      if (result.status === 'requires_2fa') {
        const query = new URLSearchParams({
          pending_token: result.data.pending_token,
          login_method: LOGIN_METHOD.EMAIL_MAGIC_LINK,
        });
        nav(`${createLanguageURL('/login/2fa')}?${query}`);
        return;
      }
      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        nav(createLanguageURL('/account'));
      }
    } catch (e: any) {
      setIsBusy(false);

      const errorData = e?.response?.data;
      if (!e?.response) console.error(e);

      if (errorData && typeof errorData === 'object' && errorData.error && errorData.message) {
        switch (errorData.error) {
          // Known codes get the translated text only; the backend message is English.
          case 'invalid_auth_method':
            setErrors([t('login.errorDifferentAuthMethod')]);
            break;
          case 'email_not_primary':
            setErrors([t('login.errorNotPrimaryEmail')]);
            break;
          case 'invalid_challenge':
          case 'challenge_mismatch':
            setErrors([t('login.invalidMagicLink')]);
            break;
          default:
            setErrors([t('login.somethingWrong'), errorData.message]);
        }
      } else {
        const errorMessage = e?.message || e?.toString() || '';
        setErrors([t('login.somethingWrong'), errorMessage]);
      }
    }
  };

  useEffect(() => {
    const login = async () => {
      const url = new URL(window.location.href);
      const hashParams = new URLSearchParams(url.hash.split('?')[1]);
      const urlParamChallenge = hashParams.get('challenge');
      const urlParamId = hashParams.get('id');

      const loginErrors = [];
      if (urlParamId && urlParamChallenge) {
        await submitChallenge(urlParamId, urlParamChallenge);
      } else {
        if (!urlParamId) {
          loginErrors.push(t('login.noIdProvided'));
        }
        if (!urlParamChallenge) {
          loginErrors.push(t('login.noChallengeProvided'));
        }
        setErrors(loginErrors);
      }
    };
    login();
  }, []);

  return (
    <Center mih="100vh" py="xl">
      <DocumentTitle title={t('login.signInLink')} />
      <Stack align="center" w="100%" maw={420} px="md">
        <Card withBorder radius="md" p={{ base: 'md', sm: 'lg' }} w="100%" shadow="xs">
          {isBusy ? (
            <Title order={1} size="h3" fw={600}>
              {t('login.processing')}
            </Title>
          ) : errors.length > 0 ? (
            <Stack gap="md">
              <Title order={1} size="h3" fw={600}>
                {t('login.somethingWrong')}
              </Title>
              {errors.map((error, index) => (
                <Text key={index} c={index === 0 ? 'red' : 'dimmed'}>
                  {error}
                </Text>
              ))}
              <Button onClick={() => nav(createLanguageURL('/login'))}>
                {t('login.backToLogin')}
              </Button>
            </Stack>
          ) : (
            <Title order={1} size="h3" fw={600}>
              {t('login.processing')}
            </Title>
          )}
        </Card>
      </Stack>
    </Center>
  );
}
